/**
 * A request waiting on a person, as it stands now, and the two things a reviewer does with it.
 *
 * A card in a conversation is a snapshot of the request when the card was written. This reads the request itself:
 * whether it is still open, who raised it, and whether the signed-in person may decide it — the server answers that
 * (`review.canReview`), so the buttons appear only for someone the server will accept.
 */
import { useMutation, useQuery, useQueryClient, type MutationFunctionContext } from '@tanstack/react-query';
import {
  approveDaemonDecisionMutation,
  approveWorkflowGateMutation,
  getDaemonDecisionOptions,
  getDaemonDecisionQueryKey,
  getWaitingOnMeQueryKey,
  rejectDaemonDecisionMutation,
  rejectWorkflowGateMutation,
} from '../generated/api/@tanstack/react-query.gen.js';
import type { DaemonDecision } from '../generated/api/types.gen.js';

export type ReviewAction = 'approve' | 'reject';
export type ReviewRoute = NonNullable<DaemonDecision['review']>['via'];

export const daemonDecisionKey = (decisionId: number) => getDaemonDecisionQueryKey({ path: { id: decisionId } });

/** How long after an approval the card keeps looking for the engine to have applied it. */
const APPLY_WATCH_MS = 60_000;
const APPLY_POLL_MS = 2_000;

/** True while an approved request is expected to be applied by the engine and has not been yet. */
export function isAwaitingApply(decision: DaemonDecision | undefined, now: number = Date.now()): boolean {
  if (decision?.reviewStatus !== 'approved' || !decision.appliesOnApproval) return false;
  if (decision.consumedAt || !decision.reviewedAt) return false;
  return now - Date.parse(decision.reviewedAt) < APPLY_WATCH_MS;
}

/**
 * The request as it stands now. `unreadable` is true when it could not be read: the card then shows what it was
 * written with and offers nothing to press, rather than passing a stale snapshot off as the present.
 */
export function useDaemonDecision(decisionId: number | null) {
  const query = useQuery({
    ...getDaemonDecisionOptions({ path: { id: decisionId ?? 0 } }),
    enabled: decisionId !== null,
    refetchInterval: (current) => (isAwaitingApply(current.state.data) ? APPLY_POLL_MS : false),
  });
  return { decision: query.data, unreadable: query.isError };
}

export interface ReviewSubmission {
  decisionId: number;
  via: ReviewRoute;
  action: ReviewAction;
  notes?: string;
}

/**
 * Approve or reject. A workflow gate is settled on its own routes, under its own policy; the request says which
 * (`review.via`). Every branch is the generated mutation of that route.
 */
export async function submitReview(
  { decisionId, via, action, notes }: ReviewSubmission,
  context: MutationFunctionContext,
): Promise<void> {
  const body = notes ? { notes } : undefined;
  const path = { id: decisionId };
  if (via === 'workflow_gate') {
    const gate = action === 'approve' ? approveWorkflowGateMutation() : rejectWorkflowGateMutation();
    await gate.mutationFn?.({ path, body: body ?? {} }, context);
    return;
  }
  const decision = action === 'approve' ? approveDaemonDecisionMutation() : rejectDaemonDecisionMutation();
  await decision.mutationFn?.({ path, ...(body ? { body } : {}) }, context);
}

export function useReviewDaemonDecision(decisionId: number | null, via: ReviewRoute) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ action, notes }: { action: ReviewAction; notes?: string }, context) => {
      if (decisionId === null) throw new Error('useReviewDaemonDecision: no decision to review');
      await submitReview({ decisionId, via, action, ...(notes ? { notes } : {}) }, context);
    },
    onSettled: async () => {
      if (decisionId !== null) await queryClient.invalidateQueries({ queryKey: daemonDecisionKey(decisionId) });
      await queryClient.invalidateQueries({ queryKey: getWaitingOnMeQueryKey() });
    },
  });
}
